import { PropertyDetail } from './property.service'

export interface PropertyFormData {
  propertyType: string
  unitSize: string
  bedrooms: string
  bathrooms: string
  floorNumber: string
  roomNumber: string
  roomPosition: string
  hasBalcony: boolean | null
  isPetFriendly: boolean | null
  maxGuests: string
  availability: string
  availableDate: any
  propertyPhotos: string[]
  videoTour: string | null
  floorPlan: string | null
  minRentalDuration: '1month' | '3-6months' | '1year' | null
  preferredRentalDuration: string
  monthlyPrice: string
  sixMonthPrice: string
  yearlyPrice: string
  securityDeposit: string
  useHaupAgent: boolean
  agentFee: string
  documents: {
    idCard: string | null
    houseRegistration: string | null
    condoOwnership: string | null
    condoJuristic: string | null
    bankBook: string | null
  }
  listingTitle: string
  shortDescription: string
  uniqueSellingPoints: string[]
  nearbyHighlights: string[]
  noiseLevel: string
  viewType: string[]
  amenities: string[]
  buildingFacilities: string[]
  locationName: string
  location: string
  locationLat: number | null
  locationLng: number | null
  nearbyBTSMRT: Array<{
    id: string
    stationId: string
    stationName: string
    walkingMinutes: string
  }>
  nearbyPopularLocations: Array<{
    id: string
    locationName: string
    walkingMinutes: string
  }>
}

export class PropertyDataMapper {
  static mapPropertyTypeToFormValue(propertyTypeName: string): string {
    const typeMapping: { [key: string]: string } = {
      Condo: 'condo',
      Apartment: 'apartment',
      House: 'house',
      Townhouse: 'townhouse',
    }
    return typeMapping[propertyTypeName] || propertyTypeName.toLowerCase()
  }

  static mapMinRentalDurationToForm(duration: string): '1month' | '3-6months' | '1year' {
    const durationMapping: { [key: string]: '1month' | '3-6months' | '1year' } = {
      MONTHLY: '1month',
      THREE_TO_SIX_MONTHS: '3-6months',
      YEARLY: '1year',
    }
    return durationMapping[duration] || '1year'
  }

  static mapAvailabilityToForm(
    availabilitySettings: { type: string; available_date?: string } | null,
  ): {
    availability: string
    availableDate: any
  } {
    if (!availabilitySettings) {
      return {
        availability: '',
        availableDate: null,
      }
    }

    if (availabilitySettings.type === 'immediate') {
      return {
        availability: 'Available immediately',
        availableDate: null,
      }
    } else {
      return {
        availability: 'Available after this date',
        availableDate: availabilitySettings.available_date
          ? new Date(availabilitySettings.available_date)
          : null,
      }
    }
  }

  static mapFeaturesToFormArrays(features: PropertyDetail['propertyFeatures']) {
    const uniqueSellingPoints: string[] = []
    const nearbyHighlights: string[] = []
    const viewType: string[] = []
    const amenities: string[] = []
    const buildingFacilities: string[] = []

    features.forEach((feature) => {
      const { type, name } = feature.featureMaster

      switch (type) {
        case 'SELLING_POINT':
          uniqueSellingPoints.push(name)
          break
        case 'NEARBY_HIGHLIGHT':
          nearbyHighlights.push(name)
          break
        case 'VIEW_TYPE':
          viewType.push(name)
          break
        case 'AMENITY':
          amenities.push(name)
          break
        case 'BUILDING_FACILITY':
          buildingFacilities.push(name)
          break
      }
    })

    return {
      uniqueSellingPoints,
      nearbyHighlights,
      viewType,
      amenities,
      buildingFacilities,
    }
  }

  static mapGalleryToForm(gallery: PropertyDetail['propertyGallery']) {
    const propertyPhotos: string[] = []
    let videoTour: string | null = null
    let floorPlan: string | null = null

    gallery
      .sort((a, b) => a.sequence - b.sequence)
      .forEach((item) => {
        switch (item.type) {
          case 'image':
            propertyPhotos.push(item.url)
            break
          case 'video':
            videoTour = item.url
            break
          case 'floor_plan':
            floorPlan = item.url
            break
        }
      })

    return { propertyPhotos, videoTour, floorPlan }
  }

  static mapPricingOptionsToForm(pricingOptions: PropertyDetail['propertyPricingOptions']) {
    let monthlyPrice = ''
    let sixMonthPrice = ''
    let yearlyPrice = ''

    pricingOptions.forEach((option) => {
      if (option.is_available) {
        switch (option.duration_type) {
          case 'MONTHLY':
            monthlyPrice = option.monthly_price
            break
          case 'THREE_TO_SIX_MONTHS':
            sixMonthPrice = option.monthly_price
            break
          case 'YEARLY':
            yearlyPrice = option.monthly_price
            break
        }
      }
    })

    return { monthlyPrice, sixMonthPrice, yearlyPrice }
  }

  static mapDocumentsToForm(documents: PropertyDetail['propertySubmissionDocuments']) {
    const formDocuments = {
      idCard: null as string | null,
      houseRegistration: null as string | null,
      condoOwnership: null as string | null,
      condoJuristic: null as string | null,
      bankBook: null as string | null,
    }

    documents.forEach((doc) => {
      switch (doc.document_type) {
        case 'ID_CARD':
          formDocuments.idCard = doc.file_url
          break
        case 'HOUSE_REGISTRATION':
          formDocuments.houseRegistration = doc.file_url
          break
        case 'OWNERSHIP_CERTIFICATE':
          formDocuments.condoOwnership = doc.file_url
          break
        case 'CONDO_JURISTIC_APPROVAL':
          formDocuments.condoJuristic = doc.file_url
          break
        case 'BANK_BOOK':
          formDocuments.bankBook = doc.file_url
          break
      }
    })

    return formDocuments
  }

  static mapNearbyTransportToForm(transports: PropertyDetail['propertyNearbyTransports']) {
    if (!transports || transports.length === 0) {
      return [{ id: 'default-1', stationId: '', stationName: '', walkingMinutes: '' }]
    }

    return transports.map((transport, index) => ({
      id: `transport-${transport.id}`,
      stationId: transport.transport_station_id.toString(),
      stationName: transport.transportStation.name,
      walkingMinutes: transport.walking_minutes.toString(),
    }))
  }

  static async convertPropertyDetailToFormData(
    propertyDetail: PropertyDetail,
    userToken?: string,
  ): Promise<Partial<PropertyFormData>> {
    const availability = this.mapAvailabilityToForm(propertyDetail.availability_settings)
    const features = this.mapFeaturesToFormArrays(propertyDetail.propertyFeatures)
    const gallery = this.mapGalleryToForm(propertyDetail.propertyGallery)
    const pricing = this.mapPricingOptionsToForm(propertyDetail.propertyPricingOptions)
    const documents = this.mapDocumentsToForm(propertyDetail.propertySubmissionDocuments)
    const nearbyTransport = this.mapNearbyTransportToForm(propertyDetail.propertyNearbyTransports)

    return {
      propertyType: this.mapPropertyTypeToFormValue(propertyDetail.propertyType.name),
      unitSize: propertyDetail.unit_size,
      bedrooms: `${propertyDetail.bedrooms} bedroom${propertyDetail.bedrooms > 1 ? 's' : ''}`,
      bathrooms: `${propertyDetail.bathrooms} bathroom${propertyDetail.bathrooms > 1 ? 's' : ''}`,
      floorNumber: propertyDetail.floor_number.toString(),
      roomNumber: propertyDetail.room_number,
      roomPosition: propertyDetail.room_position,
      hasBalcony: propertyDetail.has_balcony,
      isPetFriendly: propertyDetail.is_pet_friendly,
      maxGuests: propertyDetail.max_guests.toString(),
      ...availability,
      ...gallery,
      minRentalDuration: this.mapMinRentalDurationToForm(propertyDetail.minimum_rental_duration),
      ...pricing,
      securityDeposit:
        propertyDetail.security_deposit && parseFloat(propertyDetail.security_deposit) > 0
          ? propertyDetail.security_deposit
          : '',
      useHaupAgent: propertyDetail.use_haup_as_agent,
      agentFee:
        propertyDetail.custom_agent_fee && parseFloat(propertyDetail.custom_agent_fee) > 0
          ? propertyDetail.custom_agent_fee
          : '',
      documents,
      listingTitle: propertyDetail.name,
      shortDescription: propertyDetail.description,
      ...features,
      noiseLevel: propertyDetail.noise_level,
      locationName: propertyDetail.propertyLocation?.place_name || '',
      location: propertyDetail.propertyLocation?.address || '',
      locationLat: propertyDetail.propertyLocation
        ? parseFloat(propertyDetail.propertyLocation.lat)
        : null,
      locationLng: propertyDetail.propertyLocation
        ? parseFloat(propertyDetail.propertyLocation.lng)
        : null,
      nearbyBTSMRT: nearbyTransport,
      nearbyPopularLocations: [{ id: 'default-2', locationName: '', walkingMinutes: '' }],
    }
  }
}
